import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { updateFpAppV4Data, resetFpAppV4Data } from "@/redux/fpAppV4Slice";
import { useNavigate } from "react-router-dom";
import HistoryItem from "./HistoryItem";
import { Search } from "lucide-react";
import { Tooltip } from "@mui/material";
import {
  NoteAdd as NewWorkspaceIcon,
  Search as SearchWorkspacesIcon,
  Close as ClosePanelIcon,
} from "@mui/icons-material";
import { useWorkspaces, groupByTime } from "../../hooks/useWorkspacesSimple";
import SimpleOwnershipFilterDropdown from "../../common/SimpleOwnershipFilterDropdown";
import { useMemo, useState } from "react";

const LeftBar = ({
  closeDrawer = () => {},
  showCollapseButton = false,
  workspaceType = "private_doc_editor",
  parentAppUrl = "/chat",
  parentApp = "Chat",
}: {
  closeDrawer?: () => void;
  showCollapseButton?: boolean;
  workspaceType?: string;
  parentAppUrl?: string;
  parentApp?: string;
}) => {
  const isChatRoute = parentApp === "Chat";
  const newThreadTooltip = isChatRoute ? "New chat" : "New matrix";
  const searchTooltip = isChatRoute ? "Search chats" : "Search matrices";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaceId = useSelector(
    (state: any) => state.fpAppV4Data.workspaceId
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [threadHistorySearchTerm, setThreadHistorySearchTerm] = useState("");

  // Use our simplified hook
  const { groups, loading, error, refresh } = useWorkspaces(
    workspaceType === "querygrid" ? "matrix" : "chat",
    threadHistorySearchTerm
  );

  // Group workspaces by time for display
  const [selectedGroupName, setSelectedGroupName] = useState<string>("");

  // Filter workspaces based on selected group
  const filteredWorkspaces = useMemo(() => {
    if (groups.length === 0) return [];
    if (!selectedGroupName) return groups[0].workspaces;
    const selectedGroup = groups.find((g) => g.name === selectedGroupName);
    if (!selectedGroup) return groups[0].workspaces;
    return selectedGroup.workspaces;
  }, [groups, selectedGroupName]);

  // Group the filtered workspaces by time
  const timeGroups = useMemo(() => {
    return groupByTime(filteredWorkspaces);
  }, [filteredWorkspaces]);

  const createNewThread = () => {
    // Close FileViewer if it's open by dispatching the close event
    window.dispatchEvent(new CustomEvent("closeDocumentPanel"));

    const newWorkspaceId = uuidv4();
    dispatch(resetFpAppV4Data());
    dispatch(
      updateFpAppV4Data({
        workspaceId: newWorkspaceId,
        type: workspaceType,
        editorContentLoaded: true,
        isEditable: true,
      })
    );
    closeDrawer();
    navigate(`${parentAppUrl}/${newWorkspaceId}`);
  };

  return (
    <div className="w-[250px] h-full bg-white flex flex-col border-r border-gray-200">
      <div className="flex items-center p-2 mb-2">
        {showCollapseButton && (
          <Tooltip title="Close sidebar">
            <ClosePanelIcon
              onClick={closeDrawer}
              className="cursor-pointer text-gray-500 hover:text-gray-800"
              style={{ fontSize: 20 }}
            />
          </Tooltip>
        )}
        <div className="flex-1" />
        <div className="flex gap-4">
          <Tooltip title={searchTooltip}>
            <SearchWorkspacesIcon
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="cursor-pointer text-gray-500 hover:text-gray-800"
              style={{ fontSize: 20 }}
            />
          </Tooltip>
          <Tooltip title={newThreadTooltip}>
            <NewWorkspaceIcon
              onClick={createNewThread}
              className="cursor-pointer text-gray-500 hover:text-gray-800"
              style={{ fontSize: 20 }}
            />
          </Tooltip>
        </div>
      </div>

      {isSearchOpen && (
        <div className="p-2 mb-2">
          <span className="flex items-center p-1 px-2 border border-gray-300 rounded-xl bg-white">
            <Search size={16} className="text-gray-400 mr-2" />
            <input
              type="search"
              placeholder="Search"
              onChange={(e) => setThreadHistorySearchTerm(e.target.value)}
              value={threadHistorySearchTerm}
              className="outline-none w-full text-[12px]"
            />
          </span>
        </div>
      )}

      <div className="flex-1 overflow-auto p-2">
        {!loading && (
          <div className="px-2 mb-2">
            <SimpleOwnershipFilterDropdown
              groups={groups}
              onGroupChange={setSelectedGroupName}
            />

            <div className="mt-1 mb-1 w-3/4 border-b border-gray-300"></div>
          </div>
        )}

        {loading ? (
          <div className="w-full h-full flex justify-center">
            <div className="text-[14px] text-gray-400 px-4 mt-12">
              Loading...
            </div>
          </div>
        ) : filteredWorkspaces.length === 0 ? (
          <div className="w-full h-full flex justify-center">
            <div className="text-[14px] text-gray-400 px-4 mt-12">
              No history yet, click the center of the screen and get started!
            </div>
          </div>
        ) : (
          <>
            {timeGroups.map((timeGroup) => (
              <div key={`group_${timeGroup.title}`} className="mb-6">
                <div className="px-2 text-[13px] font-semibold text-gray-600 mb-0.75">
                  {timeGroup.title}
                </div>
                {timeGroup.workspaces.map((workspace) => (
                  <HistoryItem
                    key={workspace.id}
                    workspace={workspace}
                    currentWorkspaceId={workspaceId}
                    createNewThread={createNewThread}
                    workspaceType={workspaceType}
                    parentApp={parentApp}
                    parentAppUrl={parentAppUrl}
                    closeDrawer={closeDrawer}
                  />
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LeftBar;
